import {
  Badge,
  Button,
  Card,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  TreeSelect,
} from "antd";
import React, { memo, useCallback, useEffect } from "react";
import { useBoolean } from "../../hooks/use-boolean";
import dayjs from "dayjs";
import { FilterOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../services/axios";
import { useSearchParams } from "react-router-dom";
import { get } from "lodash";

const { TreeNode } = TreeSelect;
const { MonthPicker } = DatePicker;

interface FilterProps {
  isEmpty: boolean;
  onFilterChange: (filters: any) => void;
  isAdmin: boolean;
}

const mapTreeData = (nodes: any) => {
  return nodes.map((node: any) => (
    <TreeNode key={node.id} title={node.name} value={node.id}>
      {node.children && mapTreeData(node.children)}
    </TreeNode>
  ));
};

const Filter: React.FC<FilterProps> = ({
  isEmpty,
  onFilterChange,
  isAdmin,
}) => {
  const [form] = Form.useForm();
  const open = useBoolean();
  const [searchParams] = useSearchParams();

  const { data: formData } = useQuery({
    queryKey: ["explanation-letter-departments"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.get("/turniket/v1/bitrix/user-late-info/form");
    },
  });

  const departments = get(formData, "data.data.departments", []);
  const companies = get(formData, "data.data.companies", []);

  const getFilterFromStorage = useCallback(() => {
    const storedFilter = localStorage.getItem("EXPLANATION_LETTER_STORAGE");

    if (storedFilter) {
      try {
        const filter = JSON.parse(storedFilter);
        onFilterChange(filter);

        const defaultValues = {
          date:
            filter.year && filter.month
              ? dayjs()
                  .year(filter.year)
                  .month(filter.month - 1)
              : null,
          day: filter.day ? dayjs().date(filter.day) : null,
          departmentIds: filter.departmentIds || [],
          companyIds: filter.companyIds || [],
        };

        form.setFieldsValue(defaultValues);
      } catch (error) {
        console.error("Failed to parse filter from storage:", error);
      }
    }
  }, [form, onFilterChange]);

  const handleClearFilter = useCallback(() => {
    open.onFalse();
    form.resetFields();
    localStorage.removeItem("EXPLANATION_LETTER_STORAGE");
    onFilterChange({});
  }, [open, form, onFilterChange]);

  const handleApplyFilters = useCallback(() => {
    const values = form.getFieldsValue();
    const year = values.date ? values.date.year() : new Date().getFullYear();
    const month = values.date ? values.date.month() + 1 : 1;
    const day = values.day && values.day.date();

    const filter = {
      year,
      month,
      day,
      departmentIds: values.departmentIds || null,
      companyIds: values.companyIds || null,
    };

    localStorage.setItem("EXPLANATION_LETTER_STORAGE", JSON.stringify(filter));
    onFilterChange(filter);
    open.onFalse();
  }, [form, onFilterChange, open]);

  const dropdownContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleApplyFilters}
      initialValues={{
        clearCache: false,
        showResignEmployee: false,
        date: dayjs(),
      }}
    >
      <Card style={{ width: "400px" }}>
        {isAdmin && (
          <>
            <Form.Item label="Department" name="departmentIds">
              <TreeSelect
                allowClear
                showCheckedStrategy={TreeSelect.SHOW_ALL}
                treeCheckable={true}
                multiple
                maxTagCount={2}
                listHeight={400}
                treeDefaultExpandedKeys={["1"]}
                placeholder="Choose departments!"
                style={{ width: "100%" }}
              >
                {mapTreeData(departments)}
              </TreeSelect>
            </Form.Item>

            <Form.Item label="Company" name="companyIds">
              <TreeSelect
                allowClear
                treeCheckable={true}
                multiple
                listHeight={400}
                placeholder="Choose companies!"
                style={{ width: "100%" }}
              >
                {mapTreeData(companies)}
              </TreeSelect>
            </Form.Item>
          </>
        )}

        <Flex align="center" justify="space-between" gap={4}>
          <Form.Item
            className="w-[70%]"
            label="Select Month"
            name="date"
            rules={[{ required: true, message: "Month is required" }]}
          >
            <MonthPicker
              className="w-full"
              placeholder="Select Month and Year"
              format="YYYY-MM"
            />
          </Form.Item>

          <Form.Item className="w-[30%]" label="Select Day" name="day">
            <DatePicker
              placeholder="Day"
              format={(value) => (value ? value.format("DD") : "")}
              picker="date"
            />
          </Form.Item>
        </Flex>
        <div className="flex gap-3">
          <Button
            onClick={handleClearFilter}
            variant="filled"
            color="danger"
            className="flex-1"
          >
            Clear filter
          </Button>
          <Button type="primary" htmlType="submit" className="flex-1">
            Apply filter
          </Button>
        </div>
      </Card>
    </Form>
  );

  useEffect(() => {
    getFilterFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      placement="bottomRight"
      open={open.value}
      onOpenChange={(visible) => open.onToggle()}
    >
      <Badge dot={!isEmpty}>
        <Button icon={<FilterOutlined />} type="primary">
          Filter
        </Button>
      </Badge>
    </Dropdown>
  );
};

export default memo(Filter);
